// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useTranslation } from "react-i18next";
import type { OctoRuleDetail } from "@/api/hooks/use-octo-rules";
import { ChainLink } from "@/components/shared/chain-link";
import { CodeHighlight } from "@/components/shared/code-highlight";
import { DateTimeValue, DateValue } from "@/components/shared/datetime-value";
import { LinearIcon } from "@/components/shared/linear-icon";
import { StoreLink } from "@/components/shared/store-link";
import { Separator } from "@/components/ui/separator";
import { OctoOutcomeBadge, OctoStageBadge } from "@/features/octo/octo-rule-badges";
import { useDebugMode } from "@/lib/debug-mode";
import { linearIssueUrl, useLinearLinkMode } from "@/lib/linear";

interface OctoRuleInfoTabProps {
  rule: OctoRuleDetail;
}

export function OctoRuleInfoTab({ rule }: OctoRuleInfoTabProps) {
  const { t } = useTranslation("back-office");
  const { debugMode } = useDebugMode();
  const linkMode = useLinearLinkMode();
  const tickets = (rule.tickets ?? "")
    .split(",")
    .map((ticket) => ticket.trim())
    .filter(Boolean);

  return (
    <div className="max-w-2xl space-y-6 p-6">
      <div className="grid grid-cols-[160px_1fr] gap-y-3 text-sm">
        <span className="text-muted-foreground">{t("octoRules.fields.name")}</span>
        <span>{rule.name || <span className="text-muted-foreground italic">{t("octoRules.unnamedShort")}</span>}</span>

        <span className="text-muted-foreground">{t("octoRules.fields.description")}</span>
        <span>{rule.description || <span className="text-muted-foreground">—</span>}</span>

        <span className="text-muted-foreground">{t("octoRules.fields.scope")}</span>
        <span>
          {rule.storeName ? (
            <StoreLink storeName={rule.storeName} legibleName={rule.storeLegibleName ?? rule.storeName} />
          ) : rule.chainName ? (
            <ChainLink chainName={rule.chainName} legibleName={rule.chainLegibleName ?? rule.chainName} />
          ) : (
            <span className="text-muted-foreground">{t("octoRules.scopeGlobal")}</span>
          )}
        </span>

        <Separator className="col-span-2" />

        <span className="text-muted-foreground">{t("octoRules.fields.stage")}</span>
        <span>
          <OctoStageBadge stage={rule.stage} />
        </span>

        <span className="text-muted-foreground">{t("octoRules.fields.outcome")}</span>
        <span>
          <OctoOutcomeBadge outcome={rule.outcome} />
        </span>

        <span className="text-muted-foreground">{t("octoRules.fields.priority")}</span>
        <span className="font-mono tabular-nums">{rule.priority}</span>

        <Separator className="col-span-2" />

        <span className="text-muted-foreground">{t("octoRules.fields.predicate")}</span>
        <span className="min-w-0">
          {rule.predicate ? (
            <CodeHighlight
              value={rule.predicate}
              lang="cel"
              className="[&_pre]:min-w-0 [&_pre]:whitespace-pre-wrap [&_pre]:break-words"
            />
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </span>

        <span className="self-center text-muted-foreground">{t("octoRules.fields.tickets")}</span>
        <span>
          {tickets.length > 0 ? (
            <span className="flex flex-wrap gap-1.5">
              {tickets.map((ticket) => (
                <a
                  key={ticket}
                  href={linearIssueUrl(ticket, linkMode)}
                  target={linkMode === "web" ? "_blank" : undefined}
                  rel={linkMode === "web" ? "noreferrer" : undefined}
                  className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <LinearIcon className="size-3.5" />
                  {ticket}
                </a>
              ))}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </span>

        {/* Start/end date are not wired up in the backend yet — debug-mode only for now. */}
        {debugMode && (
          <>
            <Separator className="col-span-2" />

            <span className="text-muted-foreground">{t("octoRules.fields.startDate")}</span>
            <DateValue value={rule.startDate} />

            <span className="text-muted-foreground">{t("octoRules.fields.endDate")}</span>
            <DateValue value={rule.endDate} />
          </>
        )}

        <Separator className="col-span-2" />

        <span className="text-muted-foreground">{t("octoRules.fields.createdAt")}</span>
        <DateTimeValue value={rule.createdAt} />

        <span className="text-muted-foreground">{t("octoRules.fields.updatedAt")}</span>
        <DateTimeValue value={rule.updatedAt} />
      </div>
    </div>
  );
}
